/* import __COLOCATED_TEMPLATE__ from './playground.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import ENV from 'embercom/config/environment';
import type IntlService from 'embercom/services/intl';
import { postRequest, putRequest, request, ResponseError } from 'embercom/lib/inbox/requests';
import { dropTask } from 'ember-concurrency-decorators';
import { AsyncData } from 'embercom/resources/utils/async-data';
import { taskFor } from 'ember-concurrency-ts';
import { use } from 'ember-resources/util/function-resource';
import FinPlaygroundExport from 'embercom/lib/fin-playground-test-export';
import type RouterService from '@ember/routing/router-service';
import { AI_CHATBOT_CONTENT_CHANGED_ID } from 'embercom/services/real-time-event-service';
import { FinContentStatus } from 'embercom/lib/fin-content-status';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import scrollIntoViewIfNotVisible from 'embercom/lib/scroll-into-view-if-not-visible';
import type BrandService from 'embercom/services/brand-service';
import type LanguagesService from 'embercom/services/languages-service';
import {
  PlaygroundQuestion,
  Status,
  type PlaygroundQuestionWireFormat,
  type CustomerAnswerRating,
  type CustomerRatingReason,
} from 'embercom/lib/fin-playground/question';
import {
  QUESTION_GENERATION_UPDATE_EVENT_NAME,
  ExtractionType,
  type QuestionGenerationUpdateEvent,
  type PlaygroundQuestionGenerationJobWireFormat,
  QuestionGenerationStatus,
} from 'embercom/lib/fin-playground/question-generation';
import { TOPICS_EXPLORER_HELP_URL } from 'embercom/lib/fin-playground/topics';
import type CsvService from 'embercom/services/csv';
import { startSurvey } from 'embercom/lib/intercom-widget-helper';
import type PlaygroundApiService from 'embercom/services/playground-api';
import { assetUrl } from '@intercom/pulse/helpers/asset-url';
import type InboxSearchSuggestionsService from 'embercom/services/inbox-search-suggestions-service';
import type SessionService from 'embercom/services/session';
import type { TaskGenerator } from 'ember-concurrency';
// @ts-expect-error
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import { type SafeString } from '@ember/template/-private/handlebars';

const NexusSubscriptionEvents = [
  { eventName: AI_CHATBOT_CONTENT_CHANGED_ID, handler: '_handleContentChangedEvent' },
  { eventName: 'FinPlaygroundQuestionResponse', handler: 'handleAnswerGenerated' },
  {
    eventName: QUESTION_GENERATION_UPDATE_EVENT_NAME,
    handler: '_handleQuestionGenerationResponse',
  },
  { eventName: 'FinPlaygroundGroupUpdated', handler: 'handleGroupUpdated' },
];

const SURVEY_ID = 45346448;

interface ContentChangedEvent {
  content_available_changed_at: Date | undefined;
}

interface PlaygroundArgs {}

export default class AiAgentPlayground extends Component<PlaygroundArgs> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare notificationsService: $TSFixMe;
  @service declare realTimeEventService: $TSFixMe;
  @service declare csv: CsvService;
  @service declare router: RouterService;
  @service declare intercomEventService: $TSFixMe;
  @service declare aiContentSegmentsService: AiContentSegmentsService;
  @service declare brandService: BrandService;
  @service declare languagesService: LanguagesService;
  @service declare playgroundApi: PlaygroundApiService;
  @service declare inboxSearchSuggestionsService: InboxSearchSuggestionsService;
  @service declare session: SessionService;

  @tracked newQuestion = '';
  @tracked selectedQuestionIndex = 0;
  @tracked isAddingQuestions = false;
  @tracked showResetModal = false;
  @tracked showUploadCSVModal = false;
  @tracked isUpdatingAnswerRating = false;
  @tracked showSuggestionsSideSheet = false;
  @tracked showSettingsUpdateModal = false;
  @tracked showConfirmTestRunModal = false;
  @tracked showTestSettingsModal = false;
  @tracked testSettingsModalInSearchMode = false;
  @tracked testSettingsModalInAudienceMode = false;
  @tracked shouldRenderSuccessUi = false;
  @tracked isReasonInputFocused = false;
  @tracked teammateHasNoAccessToAllConversations = false;
  @tracked playgroundLoadingFailed = false;
  @tracked isDetailsModuleOpen = true;
  @tracked showConfirmDeleteQuestionsModal = false;
  @tracked showNewGroupModal = false;
  @tracked showDeleteGroupModal = false;
  @tracked showRenameGroupModal = false;
  @tracked showGenerateQuestionsModal = false;

  unprocessedAnswerGenerationResponses: PlaygroundQuestion[] = [];

  generateAnswersTask = taskFor(this._generateAnswers);
  generateQuestionsTask = taskFor(this._generateQuestionsTask);

  ExtractionType = ExtractionType;

  constructor(owner: unknown, args: any) {
    super(owner, args);

    this._subscribeToContentChanges();
    this.aiContentSegmentsService.loadSegments();
    if (this.brandService.isAppOnRightMultibrandPlan) {
      this.brandService.loadBrands();
    }

    if (this.router.currentRoute?.queryParams?.showExceedingQuestionLimitBanner) {
      this.showExceedingQuestionLimitNotification();
    }
  }

  willDestroy() {
    super.willDestroy();

    NexusSubscriptionEvents.forEach(({ eventName, handler }) => {
      this.realTimeEventService.off(eventName, this, handler);
    });

    this.realTimeEventService.unsubscribeTopics(['ai-chatbot-content-changed']);
  }

  _subscribeToContentChanges() {
    this.realTimeEventService.subscribeTopics(['ai-chatbot-content-changed']);
    NexusSubscriptionEvents.forEach(({ eventName, handler }) => {
      this.realTimeEventService.on(eventName, this, handler);
    });
  }

  _handleContentChangedEvent(event: ContentChangedEvent) {
    this.finContentStatusLoader.update(FinContentStatus.deserialize(event));
  }

  get impersonatedUser() {
    return this.playgroundApi.impersonatedUser;
  }

  get selectedContentSegmentIds() {
    return this.playgroundApi.selectedContentSegmentIds;
  }

  get usingAudienceTargeting() {
    return this.playgroundApi.usingAudienceTargeting;
  }

  get playground() {
    return this.playgroundApi.playground;
  }

  get playgroundIsLoading() {
    return this.playgroundApi.playgroundIsLoading;
  }

  get finContentStatus() {
    return this.finContentStatusLoader.value;
  }

  get selectedQuestion() {
    return this.playground?.filteredQuestions[this.selectedQuestionIndex];
  }

  get hasQuestions() {
    return this.playground && this.playground.questions.length > 0;
  }

  get isQuestionGenerationRunning(): boolean {
    return this.playground?.questionGeneration.isRunning ?? false;
  }

  get isQuestionGenerationRunningForApp(): boolean {
    return this.playground?.questionGeneration.isJobRunningForApp ?? false;
  }

  get isEmptyStateAndGeneratingQuestions(): boolean {
    return !this.hasQuestions && this.isQuestionGenerationRunning;
  }

  get isPlaygroundLocked(): boolean {
    return this.playgroundLoadingFailed;
  }

  get deductionRequiredToAddQuestion(): number {
    if (!this.playground?.hasReachedQuestionLimit) {
      return 0;
    }

    return 1 + this.playground.questions.length - this.playground.questionLimit;
  }

  get didAnyQuestionRunBeforeLastContentChange(): boolean {
    if (!this.playground) {
      return false;
    }

    if (
      this.playgroundApi.playgroundIsLoading ||
      this.finContentStatusLoader.isLoading ||
      this.playground.isRunning
    ) {
      return false;
    }

    let finContentChangedAt = this.finContentStatus?.contentAvailableChangedAt;
    if (!finContentChangedAt) {
      return false;
    }

    return this.playground.questions.some((question) => {
      // Technically the best way would be to compare with start of answer generation rather than `answerLastGeneratedAt`.
      if (!question.answerLastGeneratedAt) {
        return false;
      }

      // finContentChangedAt is already checked for undefined above, so we can safely assert it's presence here.
      return question.answerLastGeneratedAt <= finContentChangedAt;
    });
  }

  get isModalOpen(): boolean {
    return (
      this.showResetModal ||
      this.showUploadCSVModal ||
      this.isAddingQuestions ||
      this.showSettingsUpdateModal ||
      this.showConfirmTestRunModal ||
      this.showTestSettingsModal ||
      this.showNewGroupModal ||
      this.showDeleteGroupModal ||
      this.showRenameGroupModal
    );
  }

  get canUseArrowHotkeys() {
    return !this.isModalOpen && !this.isReasonInputFocused;
  }

  get bulkSelectedQuestionTexts(): string[] {
    return this.playground?.bulkSelectedQuestions.map((question) => question.questionText) ?? [];
  }

  get manageGroupsDropdownOptions() {
    let items = [
      {
        text: this.intl.t('ai-agent.playground.manage-groups-dropdown.settings'),
        value: 'test-as',
        onSelectItem: () => this.updateShowTestSettingsModal(true),
        icon: 'settings',
        isDisabled: this.isPlaygroundLocked,
      },
      {
        text: this.intl.t('ai-agent.playground.manage-groups-dropdown.rename-test'),
        value: 'rename-test',
        onSelectItem: this.openRenameGroupModal,
        icon: 'edit',
      },
      {
        text: this.intl.t('ai-agent.playground.manage-groups-dropdown.get-csv-report'),
        value: 'get-csv-report',
        onSelectItem: this.downloadResults,
        icon: 'download',
        isDisabled:
          this.isPlaygroundLocked ||
          this.aiContentSegmentsService.isLoading ||
          !this.playground?.hasQuestions,
      },
      {
        text: this.intl.t('ai-agent.playground.manage-groups-dropdown.delete-test'),
        value: 'delete-test',
        onSelectItem: this.openDeleteGroupModal,
        icon: 'trash',
        isDestructive: true,
      },
    ];

    let createNewGroupItem = {
      text: this.intl.t('ai-agent.playground.add-questions-dropdown.create-new-test'),
      value: 'create-new-test',
      icon: 'new',
      onSelectItem: () => this.openNewGroupModal('add_questions_dropdown'),
    };

    return [{ items }, { items: [createNewGroupItem] }];
  }

  get isQuestionGenerationDisabledByPlatform() {
    return (
      this.playgroundApi.hasDisconnectedStandalonePlatform ||
      this.playgroundApi.hasUnsupportedStandalonePlatforms
    );
  }

  getQuestionGenerationTooltip() {
    if (this.isQuestionGenerationRunningForApp) {
      return this.intl.t(
        'ai-agent.playground.blank-state.generate-from-inbox.disabled-tooltip-running',
      );
    }
    if (this.playgroundApi.hasUnsupportedStandalonePlatforms) {
      return this.intl.t(
        'ai-agent.playground.blank-state.generate-from-inbox.disabled-tooltip-unsupported',
      );
    }
    if (this.playgroundApi.hasDisconnectedStandalonePlatform) {
      return this.intl.t(
        'ai-agent.playground.blank-state.generate-from-inbox.disabled-tooltip-no-sync',
      );
    }
    return undefined;
  }

  get addQuestionDropdownOptions() {
    let inboxItems = [];
    let othersItems = [];

    // Generate from all conversations option
    let isGenerateQuestionsDisabled =
      this.isQuestionGenerationRunningForApp ||
      this.isQuestionGenerationDisabledByPlatform ||
      !this.playground?.questionGeneration.isPossible;

    let generateQuestionsTooltip =
      this.getQuestionGenerationTooltip() ||
      (!this.playground?.questionGeneration.isPossible
        ? this.intl.t('ai-agent.playground.blank-state.generate-from-inbox.disabled-tooltip')
        : undefined);

    inboxItems.push({
      text: this.intl.t(
        'ai-agent.playground.blank-state.generate-questions-from-all-conversations-more',
      ),
      value: 'generate-questions',
      isDisabled: isGenerateQuestionsDisabled,
      tooltip: generateQuestionsTooltip,
    });

    // Generate by topic option
    if (this.appService.app.canUseFinPlaygroundTopics) {
      let isGenerateByTopicDisabled =
        this.isQuestionGenerationRunningForApp ||
        this.isQuestionGenerationDisabledByPlatform ||
        !this.playground?.questionGeneration.hasTopics;

      let generateQuestionsByTopicTooltip =
        this.getQuestionGenerationTooltip() ||
        (!this.playground?.questionGeneration.hasTopics
          ? String(
              this.intl.t('ai-agent.playground.generate-dropdown.by-topic-disabled-tooltip', {
                learnMoreLinkHtml: this.learnMoreLinkHtml,
                htmlSafe: true,
              }),
            )
          : undefined);

      inboxItems.push({
        text: this.intl.t(
          'ai-agent.playground.add-questions-dropdown.generate-questions-by-topic-more',
        ),
        value: 'generate-questions-by-topic',
        isDisabled: isGenerateByTopicDisabled,
        tooltip: generateQuestionsByTopicTooltip,
        onTooltipClick:
          !this.playground?.questionGeneration.hasTopics && !this.isQuestionGenerationRunningForApp
            ? this.trackLearnMoreClick
            : undefined,
      });

      // Track when "Generate more by topic" option is visible
      this.intercomEventService.trackAnalyticsEvent({
        action: 'rendered',
        object: 'generate_by_topic_cta',
        context: 'playground',
        place: 'add_questions_dropdown',
        is_enabled: this.playground?.questionGeneration.hasTopics,
      });
    }

    othersItems.push(
      {
        text: this.intl.t('ai-agent.playground.add-questions-dropdown.upload-csv'),
        value: 'upload-csv',
      },
      {
        text: this.hasQuestions
          ? this.intl.t('ai-agent.playground.add-questions-dropdown.add-questions-manually')
          : this.intl.t('ai-agent.playground.add-question'),
        value: 'add-question',
      },
    );

    let groups = [];

    if (inboxItems.length > 0) {
      groups.push({
        heading: this.appService.app.canUseStandalone
          ? this.intl.t('ai-agent.playground.add-questions-dropdown.conversations-heading')
          : this.intl.t('ai-agent.playground.add-questions-dropdown.inbox-heading'),
        items: inboxItems,
      });
    }

    groups.push({
      heading: this.intl.t('ai-agent.playground.add-questions-dropdown.others-heading'),
      items: othersItems,
    });

    return groups;
  }

  get canIncrementQuestionIndex() {
    return (
      this.playground && this.selectedQuestionIndex < this.playground.filteredQuestions.length - 1
    );
  }

  get canDecrementQuestionIndex() {
    return this.selectedQuestionIndex > 0;
  }

  get canViewPlaygroundContent() {
    return !this.teammateHasNoAccessToAllConversations && !this.isStandaloneApp;
  }

  get hasMissingTargets(): boolean {
    if (!this.playground) {
      return false;
    }

    if (
      this.canShowBrandFilter &&
      this.playground.settings.brandId &&
      !this.brandService.brands.some((brand) => brand.id === this.playground?.settings.brandId)
    ) {
      return true;
    }

    if (
      this.playground.settings.selectedContentSegmentIds.length > 0 &&
      !this.aiContentSegmentsService.isLoading &&
      !this.playground.settings.selectedContentSegmentIds.every((id) =>
        this.aiContentSegmentsService.segments.some((segment) => segment.id === id),
      )
    ) {
      return true;
    }

    return false;
  }

  get hasAvailableExtractions() {
    if (!this.playground) {
      return false;
    }

    return (
      this.playground.questionGeneration.availableExtractions?.all ||
      this.playground.questionGeneration.availableExtractions?.lowCsat ||
      this.playground.questionGeneration.availableExtractions?.handedToTeammate
    );
  }

  @use private finContentStatusLoader = AsyncData<FinContentStatus>(async () => {
    let response = await request(`/ember/fin_content_status?app_id=${this.appService.app.id}`);
    return response.ok ? FinContentStatus.deserialize(await response.json()) : null;
  });

  get isStandaloneApp() {
    return this.appService.app.canUseStandalone;
  }

  get groupSummaries() {
    return this.playgroundApi.playgroundSummary?.groupSummaries;
  }

  get bannerImageUrl() {
    return assetUrl('/assets/images/fin/fin-playground-redesign.png');
  }

  @action
  trackLearnMoreClick(event: Event) {
    let target = event.target as HTMLElement;
    if (target.getAttribute('data-track-learn-more') === 'true') {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: 'learn_more_insights_topics_link',
        context: 'playground',
        place: 'playground_tooltip',
      });
    }
  }

  get learnMoreLinkHtml(): SafeString {
    return sanitizeHtml(
      `<a href="${TOPICS_EXPLORER_HELP_URL}" target="_blank" class="underline" data-track-learn-more="true">${this.intl.t('ai-agent.playground.generate-dropdown.learn-more')}</a>`,
    );
  }

  @action openDeleteGroupModal() {
    this.showDeleteGroupModal = true;

    this.instrumentButtonClick('open_delete_group_modal_button');
  }

  @action hideDeleteGroupModal() {
    this.showDeleteGroupModal = false;
  }

  @action openRenameGroupModal() {
    this.showRenameGroupModal = true;

    this.instrumentButtonClick('open_rename_group_modal_button');
  }

  @action hideRenameGroupModal() {
    this.showRenameGroupModal = false;
  }

  @action openNewGroupModal(place: string) {
    this.showNewGroupModal = true;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'new_group_button',
      place,
      context: 'playground',
    });
  }

  @action hideNewGroupModal() {
    this.showNewGroupModal = false;
  }

  @action
  async runBulkSelectedQuestions() {
    if (!this.playground) {
      return;
    }

    let selectedQuestions = this.playground.bulkSelectedQuestions;
    let originalStatuses = selectedQuestions.map((question) => question.status);
    try {
      this.playground.bulkSelectedQuestions.forEach((question) => {
        question.isBulkSelected = false;
        // We change the status for immediate UI feedback that questions are running
        question.status = Status.Running;
      });

      await this.generateAnswersTask.perform(selectedQuestions);
    } catch (error) {
      selectedQuestions.forEach((question, index) => {
        question.status = originalStatuses[index];
      });

      if (error instanceof ResponseError && error.response.status === 429) {
        this.notificationsService.notifyError(
          this.intl.t('ai-agent.playground.call-error.rate-limit-reached-generic.message'),
        );
        return; // graceful handling for rate limits
      }

      this.notificationsService.notifyError(
        this.intl.t('ai-agent.playground.call-error.run-questions.message'),
      );

      throw error;
    }
  }

  @action async deleteBulkSelectedQuestions() {
    if (!this.playground) {
      return;
    }

    let selectedQuestions = this.playground.bulkSelectedQuestions;
    let bulkSelectedQuestionIds = selectedQuestions
      .map((question) => question.id)
      .filter((id) => id !== undefined);

    try {
      this.playground.bulkSelectedQuestions.forEach((question) => {
        question.isBulkSelected = false;
      });
      await taskFor(this._removeQuestionsTask).perform(bulkSelectedQuestionIds);
    } catch (error) {
      selectedQuestions.forEach((question) => {
        question.isBulkSelected = false;
      });

      throw error;
    }
  }

  @action updateShouldRenderSuccessUi(shouldRender: boolean) {
    this.shouldRenderSuccessUi = shouldRender;
  }

  @action updateIsReasonInputFocused(isFocused: boolean) {
    this.isReasonInputFocused = isFocused;
  }

  @action updateShowConfirmTestRunModal(shouldRender: boolean) {
    this.showConfirmTestRunModal = shouldRender;
  }

  @action updateShowConfirmDeleteQuestionsModal(shouldRender: boolean) {
    this.showConfirmDeleteQuestionsModal = shouldRender;
  }

  @action updateShowTestSettingsModal(shouldRender: boolean) {
    this.showTestSettingsModal = shouldRender;
    if (!shouldRender) {
      this.testSettingsModalInSearchMode = false;
      this.testSettingsModalInAudienceMode = false;
    }
  }

  @action openTestSettingsModal(mode: 'settings' | 'search' | 'audience' = 'settings') {
    this.testSettingsModalInSearchMode = mode === 'search';
    this.testSettingsModalInAudienceMode = mode === 'audience';
    this.showTestSettingsModal = true;
  }

  @action handlePreviewUserSelection() {
    // Trigger confirmation flow when switching to preview user
    if (
      this.playgroundApi.impersonatedUser ||
      this.playgroundApi.selectedContentSegmentIds?.length > 0
    ) {
      // Actually update settings to preview user mode
      if (this.playground?.settings) {
        this.playground.settings.userId = null;
        this.playground.settings.removeAllSegments();
        this.playground.settings.usingAudienceTargeting = false;
      }

      // Store previous state for rollback if needed and show confirmation modal
      this.showSettingsUpdateModal = true;
    }
    // If already preview user, no action needed
  }

  @action updateIsDetailsModuleOpen(shouldOpen: boolean) {
    // This check is needed to make resizing work correctly
    // Otherwise the split instance will be reset to the default size
    // On each question change
    if (this.isDetailsModuleOpen !== shouldOpen) {
      this.isDetailsModuleOpen = shouldOpen;
    }
  }

  @action
  async clearPlayground() {
    try {
      await taskFor(this._clearPlaygroundTask).perform();
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.playground.call-error.clear-playground.message'),
      );
    }
  }

  async saveQuestions(list: string[], source: string) {
    if (!this.playground) {
      return;
    }

    let trimmedList = list.map((q) => q.trim()).filter((q) => q.length > 0);
    let uniqueQuestions = trimmedList.uniq();
    let duplicatesInInput = trimmedList.length - uniqueQuestions.length;

    let existingDuplicates = uniqueQuestions.filter((q) => this.playground?.hasQuestion(q));
    let newQuestions = uniqueQuestions.filter((q) => !this.playground?.hasQuestion(q));

    if (existingDuplicates.length > 0 || duplicatesInInput > 0) {
      let errorMessage =
        trimmedList.length === 1
          ? this.intl.t('ai-agent.playground.call-error.duplicate-questions.single')
          : this.intl.t('ai-agent.playground.call-error.duplicate-questions.multiple');

      this.notificationsService.notifyError(errorMessage);
    }

    let questions = newQuestions.map((q) => new PlaygroundQuestion(undefined, q));

    if (questions.length === 0) {
      return;
    }

    if (questions.length > this.playground.remainingQuestionQuota) {
      this.showExceedingQuestionLimitNotification();
      questions = questions.slice(0, this.playground.remainingQuestionQuota);
    }

    this.playground.addQuestions(questions);

    try {
      await taskFor(this._addQuestionsTask).perform(questions);

      // Refresh language settings after questions are saved
      await this.refreshLanguageSettings();

      this.trackAddingNewQuestions(questions.length, source);
    } catch (error) {
      questions.forEach((question) => {
        this.playground?.removeQuestion(question.questionText);
      });

      if (error instanceof ResponseError && error.response.status === 429) {
        this.notificationsService.notifyError(
          this.intl.t('ai-agent.playground.call-error.rate-limit-reached-add-question.message'),
        );

        // Rate limit errors are handled gracefully - we show the user notification
        // but don't propagate the error since the user can try again later
        return;
      } else if (error instanceof ResponseError && error.response.status === 404) {
        this.notificationsService.notifyError(
          this.intl.t('ai-agent.playground.banners.generic-reload-error'),
        );
      }

      throw error;
    }
  }

  @action
  async addQuestionsFromCsvFile(list: string[]) {
    await this.saveQuestions(list, 'upload_csv_modal');
  }

  @action
  async addQuestionsUsingForm(list: string[]) {
    try {
      await this.saveQuestions(list, 'add_questions_form');
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.playground.call-error.save-questions.message'),
      );

      throw error;
    }
  }

  @action
  async runTest() {
    await this.runQuestions();

    this.instrumentButtonClick('rerun_test');
  }

  @action
  async runQuestions() {
    if (!this.playground) {
      return;
    }

    let originalStatuses = this.playground.questions.map((question) => question.status);

    try {
      this.playground.questions.forEach(function (question) {
        // We change the status for immediate UI feedback that questions are running
        question.status = Status.Running;
      });

      await this.generateAnswersTask.perform(this.playground.pendingQuestions);
    } catch (error) {
      this.playground.questions.forEach((question, index) => {
        question.status = originalStatuses[index];
      });

      if (error instanceof ResponseError && error.response.status === 429) {
        this.notificationsService.notifyError(
          this.intl.t('ai-agent.playground.call-error.rate-limit-reached-generic.message'),
        );
        return; // graceful handling for rate limits
      }

      this.notificationsService.notifyError(
        this.intl.t('ai-agent.playground.call-error.run-questions.message'),
      );

      throw error;
    }
  }

  @action
  async runSelectedQuestion() {
    // Track batch test event for app6 to trigger outbound
    window.Intercom('trackEvent', 'batch-test-sidepanel');

    if (!this.playground || !this.selectedQuestion?.id) {
      return;
    }

    let originalStatus = this.selectedQuestion.status;

    try {
      this.playground.setQuestionState(this.selectedQuestion.id, Status.Running);

      await this.generateAnswersTask.perform([this.selectedQuestion]);
    } catch (error) {
      this.playground.setQuestionState(this.selectedQuestion.id, originalStatus);

      if (error instanceof ResponseError && error.response.status === 429) {
        this.notificationsService.notifyError(
          this.intl.t('ai-agent.playground.call-error.rate-limit-reached-generic.message'),
        );
        return; // graceful handling for rate limits
      }

      this.notificationsService.notifyError(
        this.intl.t('ai-agent.playground.call-error.run-question.message'),
      );

      throw error;
    }
  }

  @action
  async removeQuestion(question_id?: number) {
    if (!question_id) {
      return;
    }

    try {
      await taskFor(this._removeQuestionsTask).perform([question_id]);

      if (this.selectedQuestionIndex === question_id) {
        this.selectedQuestionIndex = 0;
      }
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.playground.call-error.delete-question.message'),
      );

      throw error;
    }
  }

  @action
  generateQuestions(topicId?: string) {
    this.generateQuestionsTask.perform(topicId);

    this.closeGenerateQuestionsModal();

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'generate_questions_button',
      context: 'playground',
      topic_id: topicId,
    });
  }

  @action
  resetAnswerContainerScroll() {
    let answerContainer = document.querySelector('[data-answer-container]');

    if (answerContainer) {
      answerContainer.scrollTop = 0;
    }
  }

  @action
  selectQuestion(questionText: string) {
    this.updateIsDetailsModuleOpen(true);
    this.selectedQuestionIndex =
      this.playground?.filteredQuestions.findIndex((q) => q.questionText === questionText) || 0;
    this.resetAnswerContainerScroll();
    this.updateShouldRenderSuccessUi(false);
  }

  @action incrementSelectedQuestionIndex(e?: Event) {
    e?.preventDefault();
    if (!this.canIncrementQuestionIndex) {
      return;
    }
    let index = this.selectedQuestionIndex;
    this.setQuestionIndex(index + 1);
    this.scrollToSelectedQuestion(index + 1, false);
    this.resetAnswerContainerScroll();
    this.updateShouldRenderSuccessUi(false);
  }

  @action decrementSelectedQuestionIndex(e?: Event) {
    e?.preventDefault();
    if (!this.canDecrementQuestionIndex) {
      return;
    }
    let index = this.selectedQuestionIndex;
    this.setQuestionIndex(index - 1);
    this.scrollToSelectedQuestion(index - 1, true);
    this.resetAnswerContainerScroll();
    this.updateShouldRenderSuccessUi(false);
  }

  scrollToSelectedQuestion(index: number, isScrollingUpwards: boolean) {
    let selectedQuestion = document.querySelector(`[data-question-index="${index}"]`);
    if (selectedQuestion) {
      scrollIntoViewIfNotVisible(selectedQuestion, { alignToTop: isScrollingUpwards });
    }
  }

  @action setQuestionIndex(index: number) {
    this.selectedQuestionIndex = index;
  }

  @action
  async handleAnswerGenerated(eventData: { question: PlaygroundQuestionWireFormat }) {
    let question = PlaygroundQuestion.deserialize(eventData.question);

    if (this.playground?.id !== question.groupId) {
      return;
    }

    if (this.playgroundApi.playgroundIsLoading) {
      this.unprocessedAnswerGenerationResponses.push(question);
    } else {
      this.playground?.replaceQuestionByText(question);
    }
  }

  @action
  async updateQuestionRating(
    questionId: number,
    args: {
      rating?: CustomerAnswerRating | null;
      reason?: CustomerRatingReason;
      note?: string;
    },
  ) {
    let { rating, reason, note } = args;

    this.isUpdatingAnswerRating = true;

    await taskFor(this._updateQuestionTask).perform(questionId, rating, reason, note);
    if (typeof note === 'string') {
      this.renderSuccessUiForOneSecond();
    }
    if (rating) {
      this.trackSelectingRating(rating);
    }
  }

  @action renderSuccessUiForOneSecond() {
    this.updateShouldRenderSuccessUi(true);

    setTimeout(() => {
      this.updateShouldRenderSuccessUi(false);
    }, ENV.APP._1000MS);
    return;
  }

  @action updateShowResetModal(shouldShow: boolean) {
    this.showResetModal = shouldShow;
  }

  @action
  showExceedingQuestionLimitNotification() {
    this.notificationsService.notifyWarning(
      this.intl.t('ai-agent.playground.banners.exceeding-question-limit', {
        questionLimit: this.playground?.questionLimit,
      }),
    );
  }

  @action
  downloadResults() {
    if (!this.playground) {
      return;
    }

    let { data, fileName } = new FinPlaygroundExport(
      this.appService.app,
      this.intl,
      this.playground,
      this.router,
      this.aiContentSegmentsService.segments,
      this.playgroundApi,
      this.brandService,
    );

    this.csv.export(data, {
      fileName,
      withSeparator: false,
    });

    this.intercomEventService.trackAnalyticsEvent({
      action: 'downloaded_results',
      object: 'playground',
    });
  }

  @action
  _handleQuestionGenerationResponse(e: QuestionGenerationUpdateEvent) {
    // If the selected playground doesnt match the job payload, we ignore the logic
    // for adding questions to the playground
    if (this.playground?.id && this.playground.id !== Number(e.fin_playground_group_id)) {
      this.playground.questionGeneration.updateJobWithDataForApp({
        is_job_running_for_app: e.is_job_running_for_app,
      });

      return;
    }

    this.playground?.questionGeneration.updateLatestJobData(e);

    if (e.status !== QuestionGenerationStatus.Completed) {
      return;
    }

    if (!e.questions || e.questions.length === 0) {
      return;
    }

    if (e.is_exceeding_question_limit) {
      this.showExceedingQuestionLimitNotification();
    }

    let questions = e.questions.map((question) => PlaygroundQuestion.deserialize(question));

    this.playground?.addQuestions(questions || []);

    this.trackAddingNewQuestions(e.questions.length, 'ai_bootstrap');
  }

  handleGroupUpdated(event: {
    id: string;
    group_name: string;
    last_modified_at: string;
    last_modified_by_admin: number;
  }) {
    // Only update the playground summary for the admin who made the change
    if (event.last_modified_by_admin === Number(this.session.teammate.id)) {
      this.playgroundApi.updateGroupSummaryFromRealTimeEvent(
        Number(event.id),
        event.last_modified_at,
        event.last_modified_by_admin,
      );
    }
  }

  @action
  startSurvey() {
    startSurvey(SURVEY_ID);
  }

  @dropTask
  *_addQuestionsTask(questions: PlaygroundQuestion[]) {
    if (!this.playground || questions.length === 0) {
      return;
    }

    this.playground.setRunningStatus(questions);

    let response = (yield postRequest(
      `/ember/fin_playground/questions?app_id=${this.appService.app.id}`,
      {
        questions: questions.reverse().map((q) => ({ question_text: q.questionText })),
        fin_playground_group_id: this.playground?.id,
      },
    )) as Response;

    let json = (yield response.json()) as { questions: Array<PlaygroundQuestionWireFormat> };
    for (let question of json.questions) {
      this.playground.replaceQuestionByText(PlaygroundQuestion.deserialize(question));
    }
  }

  @dropTask
  *_removeQuestionsTask(question_ids: number[]) {
    yield postRequest(
      `/ember/fin_playground/delete_questions_by_ids?app_id=${this.appService.app.id}`,
      {
        ids: question_ids,
      },
    );
    question_ids.forEach((question_id) => this.playground?.removeQuestionById(question_id));
  }

  @dropTask
  *_clearPlaygroundTask(): TaskGenerator<void> {
    let response = yield postRequest(
      `/ember/fin_playground_group/clear?app_id=${this.appService.app.id}`,
      {
        id: this.playground?.id,
      },
    );

    if (this.playground?.id && response.ok) {
      this.playgroundApi.reloadPlayground(this.playground.id);
      this.showResetModal = false;
      this.selectedQuestionIndex = 0;
    }
  }

  @dropTask
  *_updateQuestionTask(
    questionId: number,
    customerAnswerRating?: CustomerAnswerRating | null,
    customerAnswerRatingReason?: CustomerRatingReason,
    customerAnswerRatingNote?: string,
  ) {
    if (!this.playground) {
      return;
    }

    let { oldRating, oldReason, oldNote } = {
      oldRating: this.playground.getQuestionById(questionId)?.customerAnswerRating,
      oldReason: this.playground.getQuestionById(questionId)?.customerAnswerRatingReason,
      oldNote: this.playground.getQuestionById(questionId)?.customerAnswerRatingNote,
    };

    this.playground.updateQuestionRatingById(questionId, {
      rating: customerAnswerRating,
      reason: customerAnswerRatingReason,
      note: customerAnswerRatingNote,
    });

    try {
      (yield putRequest(
        `/ember/fin_playground/question/${questionId}?app_id=${this.appService.app.id}`,
        {
          customer_answer_rating: customerAnswerRating,
          customer_answer_rating_reason: customerAnswerRatingReason,
          customer_answer_rating_note: customerAnswerRatingNote,
        },
      )) as Response;
    } catch (err) {
      if (err instanceof ResponseError && err.response.status === 404) {
        this.notificationsService.notifyError(
          this.intl.t('ai-agent.playground.question-deleted-error-message'),
        );
      } else {
        this.notificationsService.notifyError(
          this.intl.t('ai-agent.playground.call-error.update-question.message'),
        );
      }

      this.playground.updateQuestionRatingById(questionId, {
        rating: oldRating,
        reason: oldReason,
        note: oldNote,
      });

      throw err;
    } finally {
      this.isUpdatingAnswerRating = false;
    }
  }

  @dropTask
  *_generateAnswers(questions?: PlaygroundQuestion[]) {
    if (!this.playground) {
      return;
    }

    this.playground.setRunningStatus(questions);

    let response = (yield postRequest(
      `/ember/fin_playground/generate_answers?app_id=${this.appService.app.id}`,
      {
        question_ids: questions?.map((q) => q.id),
        fin_playground_group_id: this.playground?.id,
      },
    )) as Response;

    let json = (yield response.json()) as { questions: Array<PlaygroundQuestionWireFormat> };
    let newQuestions = json.questions.map(PlaygroundQuestion.deserialize);
    for (let newQuestion of newQuestions) {
      this.playground.replaceQuestionByText(newQuestion);
    }

    // Refresh language settings after questions are answered
    yield this.refreshLanguageSettings();
  }

  @dropTask
  *_generateQuestionsTask(topicId?: string) {
    if (!this.playground) {
      return;
    }

    try {
      let response = (yield postRequest(
        `/ember/fin_playground/generate_questions?app_id=${this.appService.app.id}`,
        {
          fin_playground_group_id: this.playground?.id,
          extraction_type: ExtractionType.All,
          topic_id: topicId ?? null,
        },
      )) as Response;

      let json = (yield response.json()) as PlaygroundQuestionGenerationJobWireFormat;
      this.playground.questionGeneration.updateLatestJobData(json);

      // Update the lastUsedTopicId if a topic was provided
      if (topicId) {
        this.playground.lastUsedTopicId = topicId;
      }
    } catch (error) {
      // If there is a job in progress we will receive 409
      if (error instanceof ResponseError && error.response.status === 409) {
        let json = (yield error.response.json()) as PlaygroundQuestionGenerationJobWireFormat;
        this.playground.questionGeneration.updateLatestJobData(json);
      } else {
        this.notificationsService.notifyError(
          this.intl.t('ai-agent.playground.banners.generating-questions.error'),
        );
      }
    }
  }

  trackAddingNewQuestions(new_question_count: number, source: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'added_questions',
      object: 'playground',
      new_question_count,
      source,
    });
  }

  trackSelectingRating(rating: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'answer_rating',
      context: 'playground',
      rating,
    });
  }

  @action instrumentButtonClick(buttonType: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: `${buttonType}_button`,
      context: 'playground',
    });
  }

  @action instrumentIngestionBannerRender() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'rendered',
      object: 'ingestion_banner',
      context: 'playground',
    });
  }

  @action
  toggleSuggestionsSideSheet() {
    this.showSuggestionsSideSheet = !this.showSuggestionsSideSheet;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'view_suggestions_button',
      context: 'playground',
      is_open: this.showSuggestionsSideSheet,
      rating_reason: this.selectedQuestion?.customerAnswerRatingReason,
    });
  }

  @action
  selectAddQuestionDropdownOption(option: string, hideDropdownOverlay?: () => void) {
    if (hideDropdownOverlay) {
      hideDropdownOverlay();
    }

    switch (option) {
      case 'generate-questions':
        this.generateQuestions();
        break;
      case 'generate-questions-by-topic':
        this.showGenerateQuestionsModal = true;
        break;
      case 'upload-csv':
        this.showUploadCSVModal = true;
        break;
      case 'add-question':
        this.isAddingQuestions = true;
        break;
    }
  }

  @action
  openUploadCSVModal() {
    this.showUploadCSVModal = true;
  }

  @action
  showAddQuestionsManually() {
    this.isAddingQuestions = true;
  }

  @action handlePlaygroundSettingsChanged() {
    if (!this.playground || !this.playground.settings.hasChanged()) {
      return;
    }

    if (this.playground.hasQuestions) {
      this.showSettingsUpdateModal = true;
    } else {
      taskFor(this._updateSettings).perform();
    }
  }

  @action async updateSettingsAndRunQuestions() {
    this.showSettingsUpdateModal = false;

    try {
      await taskFor(this._updateSettings).perform();
    } catch (error) {
      this.notificationsService.notifyError(this.intl.t('ai-agent.playground.settings.error'));
      this.showSettingsUpdateModal = true;
      return;
    }

    await this.runQuestions();
  }

  @action async resetSettingsAndRunQuestions() {
    if (!this.playground) {
      return;
    }

    this.playground.settings.brandId = null;
    this.playground.settings.removeAllSegments();
    await taskFor(this._updateSettings).perform();
    await this.runQuestions();
  }

  @dropTask
  *_updateSettings(): TaskGenerator<void> {
    if (!this.playground) {
      return;
    }

    try {
      yield postRequest(`/ember/fin_playground/update_settings?app_id=${this.appService.app.id}`, {
        settings: {
          selected_content_segment_ids: this.playground.settings.selectedContentSegmentIds,
          brand_id: this.playground.settings.brandId,
          user_id: this.playground.settings.userId,
          using_audience_targeting: this.playground.settings.usingAudienceTargeting,
        },
        fin_playground_group_id: this.playground?.id,
      });
      this.playground.settings.refreshTrackedState();
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.playground.settings.save-success'),
      );

      yield this.playgroundApi.loadImpersonatedUser(this.playground.settings.userId);
      this.playgroundApi.setSelectedContentSegmentIds(
        this.playground.settings.selectedContentSegmentIds,
      );
      this.playgroundApi.setUsingAudienceTargeting(this.playground.settings.usingAudienceTargeting);

      this.intercomEventService.trackAnalyticsEvent({
        action: 'stored',
        object: 'playground_test_settings',
        place: 'playground_test_settings_modal',
      });
    } catch (error) {
      if (error instanceof ResponseError && error.response.status === 404) {
        this.notificationsService.notifyError(
          this.intl.t('ai-agent.playground.banners.generic-reload-error-message'),
        );
      } else {
        this.notificationsService.notifyError(this.intl.t('ai-agent.playground.settings.error'));
      }

      throw error;
    }
  }

  @action closeSettingsUpdateModal() {
    if (!this.playground) {
      return;
    }

    this.playground.settings.rollbackChanges();
    this.showSettingsUpdateModal = false;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'cancelled',
      object: 'playground_test_settings',
      place: 'playground_test_settings_modal',
    });
  }

  get canShowBrandFilter() {
    return this.brandService.isAppOnRightMultibrandPlan;
  }

  get shouldShowBrandFilter() {
    return !this.shouldUseTestSettingsModal && this.canShowBrandFilter;
  }

  get shouldUseTestSettingsModal() {
    return this.canShowBrandFilter;
  }

  @action clearFilters() {
    if (!this.playground) {
      return;
    }

    this.playground.updateQuestionRatingFilter(null);
    this.playground.updateAnswerTypeFilter(null);
  }

  private async refreshLanguageSettings() {
    await Promise.all([
      this.languagesService.refreshLanguages(),
      this.languagesService.refreshMultilingualSettings(),
    ]);
  }

  @action
  closeGenerateQuestionsModal() {
    this.showGenerateQuestionsModal = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground': typeof AiAgentPlayground;
  }
}
